import type { CurrentUserDetailsDTO, ListFeatureFlagsResponse, MyPermissionsResponse } from '@luminovo/api-contracts';
import { getToken, type Permission, permissionsFromToken, usePermissions } from '@luminovo/auth';
import { isProductionEnvironment, logToExternalErrorHandlers } from '@luminovo/commons';
import { httpQuery, suspenseHttpQuery } from '@luminovo/data-stack';
import { type QueryClient, useQueries, type UseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import { userDetailsOf } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { isOrderPortalEnabled, isPurchasingEnabled, selectFeatureFlags } from '@/shared/permissions/featureFlags';
import type { PagePermissionContext } from '@/shared/permissions/PagePermissionContext';
import { modulePermissionChecks } from '@/shared/permissions/useModulePermissions';
import { loadLocale } from '@/shared/utils/internationalisation';
import { isAppStale } from '@/shared/utils/staleApp';

/** Loaded by `_authenticated`'s `beforeLoad` before any signed-in page renders: `context.session` below it. */
export type Session = PagePermissionContext;

/**
 * Never collected or stale with time: every navigation's `beforeLoad` reads them from the cache, as a refetch or a
 * collected one would hold each navigation for a round trip. While a signed-in page is open, `useWatchSession` keeps
 * them cached and up to date.
 */
const sessionQuery = { gcTime: Infinity, staleTime: Infinity };

/**
 * The queries the session is made of, with the keys the components read them with. Only `/my/*` endpoints belong
 * here: every signed-in page waits for each of them, and when one fails no page opens. Anything else `ensureSession`
 * prefetches, or the page that needs it asks for.
 */
function sessionQueries() {
    return {
        details: suspenseHttpQuery('GET /my/details', {}, sessionQuery),
        permissions: suspenseHttpQuery('GET /my/permissions', {}, sessionQuery),
        featureFlags: suspenseHttpQuery('GET /my/feature-flags', {}, sessionQuery),
    };
}

/**
 * Loads the session with the queries and keys the components read, so they share the cache: in one round trip on a
 * cold load, from the cache on every navigation after. The API token must be set.
 * Except for a `preload`, activates the user's language as their details arrive, so the first page renders in it; a
 * catalog that fails is reported and the active language stays (`loadSessionLanguage`).
 */
export async function ensureSession(queryClient: QueryClient, { preload }: { preload: boolean }): Promise<Session> {
    // What many pages read, not awaited: started when the pages render, they would take another round trip.
    // The customer portal settings: the navbar's footer links and who may view PnP files (`useCanViewPnp`).
    prefetchUnlessCached(queryClient, httpQuery('GET /customer-portal-settings', {}));
    prefetchUnlessCached(queryClient, httpQuery('GET /suppliers-and-stock-locations', {}));
    prefetchUnlessCached(queryClient, httpQuery('GET /organization-settings/organization-currency-settings', {}));
    prefetchUnlessCached(queryClient, httpQuery('GET /sourcing-templates', {}));
    prefetchUnlessCached(queryClient, httpQuery('GET /manufacturers', { queryParams: { high_quality: false } }));

    const queries = sessionQueries();
    const [details, myPermissions, featureFlags] = await Promise.all([
        queryClient.ensureQueryData(queries.details).then(async ({ data }) => {
            if (!preload) {
                await loadSessionLanguage(data.user.user_language);
            }
            return data;
        }),
        queryClient.ensureQueryData(queries.permissions),
        queryClient.ensureQueryData(queries.featureFlags),
    ]);

    return sessionOf({
        // From the token the pages hold now, which `AuthenticatedContextProvider` renews; the session never keeps it.
        permissions: permissionsFromToken(getToken()),
        details,
        myPermissions,
        featureFlags,
    });
}

/**
 * The session for a component below `_authenticated`, from the queries `ensureSession` loads. Call a predicate with it
 * for a check that isn't "may open this page", such as an action or edit right. A link or a button that opens a page
 * asks the route through `useCanOpen` instead: a predicate skips the layouts above the page and its
 * `modulePermissions`. The queries are in the cache before any signed-in page renders; it suspends only where they
 * aren't, as in a test that renders a component on its own.
 */
export function useSession(): Session {
    const { permissions } = usePermissions();
    // One query at a time: in the app they are all cached, so none of them waits.
    const queries = sessionQueries();
    const { data: details } = useSuspenseQuery(queries.details);
    const { data: myPermissions } = useSuspenseQuery(queries.permissions);
    const { data: featureFlags } = useSuspenseQuery(queries.featureFlags);

    return React.useMemo(
        () => sessionOf({ permissions, details: details.data, myPermissions, featureFlags }),
        [permissions, details, myPermissions, featureFlags],
    );
}

/** The session, from the token's permissions and what its queries answered: `ensureSession`'s and `useSession`'s. */
function sessionOf({
    permissions,
    details,
    myPermissions: { module_permissions: modulePermissions, action_permissions: actionPermissions },
    featureFlags,
}: {
    permissions: Permission[];
    details: CurrentUserDetailsDTO;
    myPermissions: MyPermissionsResponse;
    featureFlags: ListFeatureFlagsResponse;
}): Session {
    const currentUser = userDetailsOf(details);
    const isAdvancedRoleManagementEnabled = featureFlags.is_advanced_role_management_enabled;
    const { hasPermissionForAction } = modulePermissionChecks({
        modulePermissions,
        actionPermissions,
        isAdvancedRoleManagementEnabled,
    });
    const enabledFeatureFlags = selectFeatureFlags(featureFlags);

    return {
        permissions,
        modulePermissions,
        isAdvancedRoleManagementEnabled,
        hasPermissionForAction,
        currentUser,
        userType: currentUser.userType,
        productCostingAccess: featureFlags.product_costing_access,
        isFeatureFlagEnabled: (flag) => enabledFeatureFlags[flag],
        isOrderPortalEnabled: isOrderPortalEnabled(),
        isPurchasingEnabled: isPurchasingEnabled(),
        isProductionEnvironment: isProductionEnvironment(),
    };
}

/**
 * Watches the session's queries from `AuthenticatedLayout`, without re-rendering on change. Unwatched, the sweep after
 * a DELETE (`useHttpMutation`) would remove them and hold the next navigation, and invalidating them wouldn't refetch.
 */
export function useWatchSession(): void {
    useQueries({
        queries: Object.values(sessionQueries()).map((query) => ({ ...query, notifyOnChangeProps: [] })),
    });
}

let sessionLanguage: { language: string; loaded: Promise<void> } | undefined;

/**
 * Once per language: every navigation runs `ensureSession`, query-string writes included, and one that replaces a
 * waiting navigation gets the same promise. Asking again would only re-activate a loaded catalog, or re-report a
 * failed one, which stays failed until the page reloads.
 */
function loadSessionLanguage(language: string): Promise<void> {
    if (sessionLanguage?.language !== language) {
        sessionLanguage = {
            language,
            loaded: loadLocale(language).catch((error: unknown) => {
                // A stale app lost the catalog with the last deploy, which `installStaleAppDetection` has logged.
                if (!isAppStale()) {
                    logToExternalErrorHandlers(error instanceof Error ? error : new Error(String(error)));
                }
            }),
        };
    }
    return sessionLanguage.loaded;
}

/** Fetches `query` in the background while the cache has nothing for it, as `usePrefetchQuery` does. */
function prefetchUnlessCached<TQueryFnData, TData>(
    queryClient: QueryClient,
    query: UseQueryOptions<TQueryFnData, Error, TData>,
): void {
    if (queryClient.getQueryState(query.queryKey) === undefined) {
        void queryClient.prefetchQuery(query);
    }
}
